import { Deserializable } from './deserializable.model';
import { Region } from './enums';

export interface Entitlement {
  value: string;
  is_unlimited: boolean;
  is_overridden_by_subscription: boolean;
  has_addon_applied: boolean;
  is_trial_value: boolean;
  post_trial_value: string;
  has_conflicts: boolean;
  is_failsafe_value: boolean;
}

export interface PrivacySettings {
  extended_previews_allowed: number;
  microsoft_editor_allowed: number;
  download_accelerator_allowed: number;
  link_compatibility_allowed: number;
  media_streaming_allowed: number;
  extended_web_thumbnails_allowed: number;
}

export interface FeatureFlagState {
  enabled: boolean;
  payload?: Record<string, any> | null;
}

export class User implements Deserializable<User> {
  // every variable must be initialized with a default value
  // or the constructor will not assign the value
  public exists = 0;
  public id = 0;
  public uid = 0;
  public team_id = 0;
  public plan_id = 0;
  public plan_name = 'Sync.com Free';
  public account_type = 'Sync Starter';
  public account_duedate = 0;
  public renewal_plan_id = 0;
  public getstarted_completed = 0;
  public display_name = '';
  public dashboard = '';
  public email = '';
  public first_name = '';
  public last_name = '';
  public password_hint = '';
  public join_date = 0;

  public is_office = 0; // Uses MS Office Integration (wopi)
  public is_displayedofficeoptin = 0; // Uses MS Office Integration (wopi)

  public has_apps = 0; // Has installed an app at some point
  public app_count = 0; // Currently active apps

  public is_office_personal = 0;

  public is_verified = 0;
  public is_pro = 0;
  public is_multi_admin = false;
  public is_multi_child = false;
  public is_multi_new = false;
  public is_on_trial = false;
  public is_trial_used = false;
  public permissions: Record<string, boolean | number> = {};
  public is_resetpassword = 0;
  public is_disablesso = 0;

  public is_disablepurge = 0;
  public is_disableautorenew = 0;
  public is_emailmarketing = 1;
  public is_emailproduct = 1;
  public is_emailrewards = 1;

  public is_unlimited_plan = 0;
  public is_disableanalytics = 0;
  public is_newui_disabled = 0;

  public disk_limit_bytes = 0;
  public disk_usage_bytes = 0;
  public disk_usage_percent = 0;

  public web_device_id = 0;

  public syncus_sync_id = 0;
  public web_sync_id = 0;

  public install_mobile = 0;
  public install_desktop = 0;

  public pubkey_id = 0;
  public pubkey = '';
  public sessionttl = 0;
  public login_servtime = 0;

  public referral_code = '';
  public has_2fa = 0;
  public is_google_app_2fa_disabled = 0;
  public has_files = 0;
  public has_gettingstarted = 0;
  public has_links = 0;
  public has_password_hint = 0;
  public has_shares = 0;
  public has_beta_access = 0;

  public twofa_type = '0';
  public theme = '';

  public backup_code_count = 0;

  public zuora_acct_key = '';
  public sku = '';
  public plan_sku = '';
  public plan_product_id = '';
  public plan_rate_plan_id = '';
  public plan_rate_plan_charge_id = '';
  public plantype_id = 1; // default plan type 1 = free
  public plan_user_limit = 1; // default of 1 user limit means non-multi admin
  public entitlements: Record<string, Entitlement> = {};
  public is_free_plan = 0;
  public region: Region = Region.CACENTRAL;
  public privacy_settings: PrivacySettings = {
    extended_previews_allowed: 1,
    microsoft_editor_allowed: 1,
    download_accelerator_allowed: 1,
    link_compatibility_allowed: 1,
    media_streaming_allowed: 1,
    extended_web_thumbnails_allowed: 1,
  };
  public feature_flags: Record<string, FeatureFlagState> = {};

  //whitelabel attributes
  public whitelabel = {
    image_data: '',
    image_cachekey: '',
    header_primary_color: '',
    header_text_color: '',
    button_primary_color: '',
    button_text_color: '',
    link_text_color: '',
  };

  public avatar = {
    avatar_data: '',
    avatar_cachekey: '',
  };

  public getstarted = {
    hasVerified: 0,
    hasPasswordHint: 0,
    hasMobile: 0,
    hasDeviceOne: 0,
    hasDeviceMany: false,
    hasFiles: 0,
    hasShares: 0,
    hasLinks: 0,
  };

  constructor(params?: { [k: string]: any }) {
    if (params) {
      this.deserialize(params);
    }
  }

  public deserialize(params: any): User {
    for (const [key, val] of Object.entries(params)) {
      if (key in this) {
        this[key] = val;
      }
    }
    this.account_type = params.plan_id > 1 || params.plan_product_id ? 'Sync Pro' : 'Sync Starter';

    // handle when 2fa type is set to null, change it to 0 string.
    if (params.twofa_type === undefined || params.twofa_type == null) {
      this.twofa_type = '0';
    }

    if (params.zuora_acct_key) {
      this.is_multi_child = params.is_multi_child === 1;
      this.is_multi_admin = params.is_multi_admin === 1;
    } else {
      if (
        params.plan_id === 1001 ||
        params.plan_id === 1101 ||
        params.plan_id === 1201 ||
        params.plan_id === 1301 ||
        params.plan_id === 1401 ||
        params.plan_id === 1501
      ) {
        this.is_multi_child = true;
      } else if (params.plan_id > 1000) {
        this.is_multi_admin = true;
      }
    }

    //build the whitelabel data
    this.whitelabel.image_data = params.image_data;
    this.whitelabel.image_cachekey = params.image_cachekey;
    this.whitelabel.header_primary_color = params.header_primary_color;
    this.whitelabel.header_text_color = params.header_text_color;
    this.whitelabel.button_primary_color = params.button_primary_color;
    this.whitelabel.button_text_color = params.button_text_color;
    this.whitelabel.link_text_color = params.link_text_color;

    //build avatar data
    this.avatar.avatar_data = params.avatar_data;
    this.avatar.avatar_cachekey = params.avatar_cachekey;

    // build the getstarted data
    this.getstarted.hasVerified = params.is_verified;
    this.getstarted.hasPasswordHint = params.has_password_hint;
    this.getstarted.hasMobile = params.install_mobile;
    this.getstarted.hasDeviceOne = params.install_desktop;
    this.getstarted.hasDeviceMany = params.install_desktop > 1;
    this.getstarted.hasFiles = params.has_files;
    this.getstarted.hasShares = params.has_shares;
    this.getstarted.hasLinks = params.has_links;
    let cnt = 0;
    for (const key in this.getstarted) {
      if (this.getstarted.hasOwnProperty(key) && this.getstarted[key]) {
        cnt++;
      }
    }

    this.getstarted_completed = cnt;

    return this;
  }
}
